import { useState, useRef, useCallback } from 'react';
import { submitQuestionAnswer, resolveChallengeQuestion, triggerChallengeQuestion } from '../../services/quizService';
import { toast } from 'sonner';

/**
 * Custom hook to manage the Challenge Questions verification workflow.
 * Handles answer submission on question transitions, checking for challenge_pending indicators,
 * resolving/answering verification events, and pulling active challenges if a session is blocked.
 */
export default function useChallengeQuestions({
  sessionId,
  currentQuestionIndex,
  questions,
  answers,
  setDirection,
  setCurrentQuestionIndex,
  setSubmittedQuestions,
  setIsSubmitting,
}) {
  const pendingTransitionIndexRef = useRef(null);
  const [activeChallenge, setActiveChallenge] = useState(null);

  const handleChallengeResult = useCallback((res) => {
    setActiveChallenge(null);

    if (res?.is_violation) {
      toast.error("Verification failed. A proctoring violation has been logged.");
    } else if (res?.result === "correct") {
      toast.success("Verification successful!");
    }

    if (pendingTransitionIndexRef.current !== null) {
      const { targetIndex, directionVal } = pendingTransitionIndexRef.current;
      setDirection(directionVal);
      setCurrentQuestionIndex(targetIndex);
      pendingTransitionIndexRef.current = null;
    }
  }, [setDirection, setCurrentQuestionIndex]);

  const saveAnswerAndNavigate = useCallback(async (targetIndex, directionVal = 1) => {
    if (!sessionId || currentQuestionIndex < 0) {
      setDirection(directionVal);
      setCurrentQuestionIndex(targetIndex);
      return;
    }

    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) {
      setDirection(directionVal);
      setCurrentQuestionIndex(targetIndex);
      return;
    }

    const questionId = currentQuestion.id;
    const answer = answers[questionId] || {
      question: questionId,
      type: currentQuestion.question_type || 'objective',
    };

    try {
      setIsSubmitting(true);
      const res = await submitQuestionAnswer(sessionId, answer);
      setSubmittedQuestions(prev => new Set([...prev, questionId]));

      if (res?.challenge_pending && res?.challenge_event) {
        pendingTransitionIndexRef.current = { targetIndex, directionVal };
        setActiveChallenge(res.challenge_event);
      } else {
        setDirection(directionVal);
        setCurrentQuestionIndex(targetIndex);
      }
    } catch (err) {
      console.error('Failed to submit answer / check challenge:', err);

      const errCode = err?.response?.data?.code;
      if (errCode === 'pending_challenge_exists') {
        try {
          const challengeData = await triggerChallengeQuestion(sessionId);
          pendingTransitionIndexRef.current = { targetIndex, directionVal };
          setActiveChallenge(challengeData);
          return;
        } catch (fetchErr) {
          console.error('Failed to fetch pending challenge details:', fetchErr);
        }
      }

      setDirection(directionVal);
      setCurrentQuestionIndex(targetIndex);
    } finally {
      setIsSubmitting(false);
    }
  }, [sessionId, currentQuestionIndex, questions, answers, setDirection, setCurrentQuestionIndex, setSubmittedQuestions, setIsSubmitting]);

  const handleResolveChallenge = useCallback(async (eventId, responseValue) => {
    if (!sessionId) return;

    try {
      const res = await resolveChallengeQuestion(sessionId, eventId, "answer", responseValue);
      handleChallengeResult(res);
    } catch (err) {
      console.error("Failed to resolve challenge:", err);
      toast.error("Failed to submit challenge verification.");
    }
  }, [sessionId, handleChallengeResult]);

  const handleChallengeTimeout = useCallback(async (eventId) => {
    if (!sessionId) return;

    try {
      const res = await resolveChallengeQuestion(sessionId, eventId, "timeout");
      handleChallengeResult(res);
    } catch (err) {
      console.error("Failed to record challenge timeout:", err);
    }
  }, [sessionId, handleChallengeResult]);

  return {
    activeChallenge,
    saveAnswerAndNavigate,
    handleResolveChallenge,
    handleChallengeTimeout,
  };
}
